/* ==========================================================================
   LIORA motion engine — GSAP-style polish with zero dependencies.
   IntersectionObserver + requestAnimationFrame + Web Animations API only.

   Motion mode is set on <html data-liora-motion> by snippets/liora-theme-vars:
     full  – everything below
     soft  – fades, hovers and count-ups only (no parallax / large movement)
     none  – nothing
     off   – merchant disabled animations (hovers still work via CSS)
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  var MODE = root.getAttribute('data-liora-motion') || 'full';
  var FULL = MODE === 'full';
  var ANY = MODE === 'full' || MODE === 'soft';
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var EASE = 'cubic-bezier(.22,1,.36,1)';

  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; fn(); });
    };
  }

  /* ------------------------------------------------------------------
     1. Heading split: words rise from a mask, staggered
     ------------------------------------------------------------------ */
  var SPLIT_SELECTOR = [
    '.liora-section-head .liora-heading',
    '.liora-newsletter__heading',
    '.liora-story__heading',
    '.liora-cta__heading',
    '.liora-faq__intro .liora-heading',
    '.liora-look__heading',
    '.liora-reviews__top .liora-heading',
    '.liora-trust__intro .liora-heading',
    '.liora-page-hero .liora-heading',
    '.liora-page-head .liora-heading',
    '.liora-collection-count .liora-heading',
    '.liora-featured .section__heading',
    '.liora-collection-hero .collection-header__title'
  ].join(',');

  var splitObserver = null;

  function splitNode(node, counter) {
    var children = Array.prototype.slice.call(node.childNodes);
    children.forEach(function (child) {
      if (child.nodeType === 3) {
        var parts = child.textContent.split(/(\s+)/);
        var frag = document.createDocumentFragment();
        parts.forEach(function (part) {
          if (!part) return;
          if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(' ')); return; }
          var outer = document.createElement('span');
          outer.className = 'liora-word';
          var inner = document.createElement('span');
          inner.className = 'liora-word__in';
          inner.style.setProperty('--i', counter.n++);
          inner.textContent = part;
          outer.appendChild(inner);
          frag.appendChild(outer);
        });
        node.replaceChild(frag, child);
      } else if (child.nodeType === 1 && child.tagName !== 'BR' && child.tagName !== 'SVG' && !child.classList.contains('liora-eyebrow')) {
        splitNode(child, counter);
      }
    });
  }

  function initSplits(scope) {
    if (!FULL) return;
    (scope || document).querySelectorAll(SPLIT_SELECTOR).forEach(function (el) {
      if (el.hasAttribute('data-liora-split')) return;
      var text = el.textContent.replace(/\s+/g, ' ').trim();
      if (!text) return;
      el.setAttribute('data-liora-split', '');
      el.setAttribute('aria-label', text);
      splitNode(el, { n: 0 });
      Array.prototype.forEach.call(el.children, function (c) { c.setAttribute('aria-hidden', 'true'); });
      if (!splitObserver) {
        splitObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { e.target.classList.add('is-inview'); splitObserver.unobserve(e.target); }
          });
        }, { threshold: 0, rootMargin: '0px 0px 4% 0px' });
      }
      splitObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     2. Auto-reveal for Minimog grids, footer and PDP blocks
     ------------------------------------------------------------------ */
  function tagReveals(scope) {
    if (!ANY) return;
    scope = scope || document;
    var groups = [
      { sel: '.sf-grid > .sf-column, .sf-grid .swiper-wrapper > .sf-column', cols: 4, step: 70 },
      { sel: '.sf__product-listing .sf__col-item', cols: 4, step: 60 },
      { sel: '.sf-footer .sf-footer__block', cols: 5, step: 80 },
      { sel: '.template-product .main-product__block', cols: 99, step: 45 },
      { sel: '.liora-filter-pill', cols: 99, step: 35 },
      { sel: '.m-search-item', cols: 6, step: 40 }
    ];
    groups.forEach(function (g) {
      var nodes = scope.querySelectorAll(g.sel);
      nodes.forEach(function (el, i) {
        if (el.hasAttribute('data-liora-reveal') || el.closest('[data-liora-anim="off"]')) return;
        if (el.closest('.swiper-container-initialized')) { return; } // never hide slides Swiper already positioned
        el.setAttribute('data-liora-reveal', 'auto');
        el.style.setProperty('--liora-delay', Math.min((i % g.cols) * Math.min(g.step, 45), 180) + 'ms');
      });
    });
    if (window.LioraTheme && window.LioraTheme.scanReveals) window.LioraTheme.scanReveals(scope);
  }

  /* ------------------------------------------------------------------
     3. Parallax (translate only, composes with hover scale)
     ------------------------------------------------------------------ */
  var PARALLAX_SELECTOR = '.liora-hero__media .liora-media, .liora-look__visual .liora-media, .liora-editorial-card__media .liora-media, .liora-page-hero__media .liora-media, .liora-collection-hero__media .liora-media, .liora-review-visual .liora-media';
  var parallaxActive = new Set();
  var parallaxObserver = null;

  function updateParallax() {
    var vh = window.innerHeight;
    parallaxActive.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.bottom < 0 || r.top > vh) return;
      var progress = (vh - r.top) / (vh + r.height); // 0 → 1 through the viewport
      var range = Math.min(r.height * 0.09, 56);
      el.style.setProperty('--liora-py', ((progress - 0.5) * -2 * range).toFixed(1) + 'px');
    });
  }

  function initParallax(scope) {
    if (!FULL) return;
    (scope || document).querySelectorAll(PARALLAX_SELECTOR).forEach(function (el) {
      if (el.hasAttribute('data-liora-parallax')) return;
      el.setAttribute('data-liora-parallax', '');
      if (!parallaxObserver) {
        parallaxObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) parallaxActive.add(e.target); else parallaxActive.delete(e.target);
          });
          updateParallax();
        }, { rootMargin: '10% 0px 10% 0px' });
      }
      parallaxObserver.observe(el);
    });
  }

  /* ------------------------------------------------------------------
     4. Hero content drifts and fades as you scroll away
     ------------------------------------------------------------------ */
  function updateHero() {
    var hero = document.querySelector('.liora-hero');
    if (!hero) return;
    var h = hero.offsetHeight || 1;
    var p = Math.max(0, Math.min(1, window.scrollY / (h * 0.85)));
    hero.style.setProperty('--liora-hero-p', p.toFixed(3));
  }

  /* ------------------------------------------------------------------
     5. Scroll progress line
     ------------------------------------------------------------------ */
  var progressEl = null;
  function updateProgress() {
    if (!progressEl) return;
    var max = document.documentElement.scrollHeight - window.innerHeight;
    progressEl.style.transform = 'scaleX(' + (max > 0 ? (window.scrollY / max) : 0).toFixed(4) + ')';
  }
  function initProgress() {
    if (!FULL || progressEl || document.body.getAttribute('data-liora-progress') === 'false') return;
    progressEl = document.createElement('div');
    progressEl.className = 'liora-progress';
    progressEl.setAttribute('aria-hidden', 'true');
    document.body.appendChild(progressEl);
  }

  /* ------------------------------------------------------------------
     6. Count-up numbers
     ------------------------------------------------------------------ */
  var countObserver = null;
  function animateCount(el) {
    var original = el.textContent.trim();
    var match = original.match(/-?\d[\d,]*\.?\d*/);
    if (!match) return;
    var raw = match[0];
    var target = parseFloat(raw.replace(/,/g, ''));
    if (!isFinite(target)) return;
    var decimals = (raw.split('.')[1] || '').length;
    var useComma = raw.indexOf(',') > -1;
    var prefix = original.slice(0, match.index);
    var suffix = original.slice(match.index + raw.length);
    var duration = 1500;
    var start = null;
    function format(v) {
      var s = v.toFixed(decimals);
      if (useComma) { var p = s.split('.'); p[0] = p[0].replace(/\B(?=(\d{3})+(?!\d))/g, ','); s = p.join('.'); }
      return prefix + s + suffix;
    }
    el.style.fontVariantNumeric = 'tabular-nums';
    function frame(t) {
      if (start === null) start = t;
      var k = Math.min(1, (t - start) / duration);
      var eased = 1 - Math.pow(1 - k, 4);
      el.textContent = format(target * eased);
      if (k < 1) requestAnimationFrame(frame); else el.textContent = original;
    }
    requestAnimationFrame(frame);
  }
  function initCounts(scope) {
    if (!ANY) return;
    (scope || document).querySelectorAll('.liora-trust__value, .liora-review-metric__value, .liora-score__number').forEach(function (el) {
      if (el.hasAttribute('data-liora-count')) return;
      el.setAttribute('data-liora-count', '');
      if (!countObserver) {
        countObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) { animateCount(e.target); countObserver.unobserve(e.target); }
          });
        }, { threshold: 0.6 });
      }
      countObserver.observe(el);
    });
    // progress line on the metric card grows with it
    (scope || document).querySelectorAll('.liora-review-metric__line i').forEach(function (bar) {
      if (bar.hasAttribute('data-liora-bar')) return;
      bar.setAttribute('data-liora-bar', '');
    });
  }

  /* ------------------------------------------------------------------
     7. Magnetic buttons (fine pointers only)
     ------------------------------------------------------------------ */
  function initMagnetic() {
    if (!FULL || !finePointer) return;
    var current = null;
    document.addEventListener('pointermove', function (e) {
      var el = e.target.closest ? e.target.closest('.liora-button, .liora-rail-btn, .liora-newsletter__button') : null;
      if (current && current !== el) { current.style.setProperty('--mx', '0px'); current.style.setProperty('--my', '0px'); current = null; }
      if (!el) return;
      current = el;
      var r = el.getBoundingClientRect();
      var x = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
      var y = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
      el.style.setProperty('--mx', (x * 6).toFixed(1) + 'px');
      el.style.setProperty('--my', (y * 4).toFixed(1) + 'px');
    }, { passive: true });
    document.addEventListener('pointerleave', function () {
      if (current) { current.style.setProperty('--mx', '0px'); current.style.setProperty('--my', '0px'); current = null; }
    });
  }

  /* ------------------------------------------------------------------
     8. Drag-to-scroll rails with a little inertia (desktop mouse)
     ------------------------------------------------------------------ */
  function initDragRails(scope) {
    if (!finePointer) return;
    (scope || document).querySelectorAll('[data-liora-rail]').forEach(function (rail) {
      if (rail.hasAttribute('data-liora-drag')) return;
      rail.setAttribute('data-liora-drag', '');
      var down = false, startX = 0, startLeft = 0, lastX = 0, velocity = 0, moved = false, raf = null;
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || rail.scrollWidth <= rail.clientWidth + 2) return;
        down = true; moved = false; startX = lastX = e.clientX; startLeft = rail.scrollLeft; velocity = 0;
        if (raf) cancelAnimationFrame(raf);
      });
      window.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (Math.abs(dx) > 4 && !moved) { moved = true; rail.classList.add('is-dragging'); }
        if (!moved) return;
        velocity = e.clientX - lastX; lastX = e.clientX;
        rail.scrollLeft = startLeft - dx;
      }, { passive: true });
      window.addEventListener('pointerup', function () {
        if (!down) return;
        down = false;
        if (!moved) return;
        var v = velocity;
        (function glide() {
          if (Math.abs(v) < 0.5) { rail.classList.remove('is-dragging'); return; }
          rail.scrollLeft -= v; v *= 0.93; raf = requestAnimationFrame(glide);
        })();
      });
      rail.addEventListener('click', function (e) { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } }, true);
      rail.addEventListener('dragstart', function (e) { e.preventDefault(); });
    });
  }

  /* ------------------------------------------------------------------
     9. Smooth <details> accordions (FAQ)
     ------------------------------------------------------------------ */
  function initDetails(scope) {
    if (!ANY || !('animate' in Element.prototype)) return;
    (scope || document).querySelectorAll('details.liora-faq__item').forEach(function (d) {
      if (d.hasAttribute('data-liora-details')) return;
      d.setAttribute('data-liora-details', '');
      var summary = d.querySelector('summary');
      var content = d.querySelector('.liora-faq__answer');
      if (!summary || !content) return;
      d.removeAttribute('name'); /* the native exclusive group would snap siblings shut without the animation */
      var anim = null;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (anim) anim.cancel();
        var opening = !d.open;
        if (opening && d.parentElement) {
          d.parentElement.querySelectorAll('details.liora-faq__item[open]:not(.is-closing)').forEach(function (o) {
            var os = o !== d && o.querySelector('summary'); if (os) os.click();
          });
        }
        var startH = d.offsetHeight;
        if (opening) d.open = true;
        var endH = opening ? summary.offsetHeight + content.offsetHeight : summary.offsetHeight;
        d.style.overflow = 'hidden';
        d.classList.toggle('is-closing', !opening);
        anim = d.animate({ height: [startH + 'px', endH + 'px'] }, { duration: 420, easing: EASE });
        anim.onfinish = anim.oncancel = function () {
          if (!opening) d.open = false;
          d.style.overflow = ''; d.classList.remove('is-closing'); anim = null;
        };
      });
    });
  }

  /* ------------------------------------------------------------------
     10. Pointer-follow tilt highlight on editorial media (very subtle)
     ------------------------------------------------------------------ */
  function initSpotlight() {
    if (!FULL || !finePointer) return;
    document.addEventListener('pointermove', function (e) {
      var card = e.target.closest ? e.target.closest('.liora-editorial-card, .liora-category-card, .liora-look__visual') : null;
      if (!card) return;
      var r = card.getBoundingClientRect();
      card.style.setProperty('--sx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
      card.style.setProperty('--sy', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
    }, { passive: true });
  }

  /* ------------------------------------------------------------------
     Boot + lifecycle
     ------------------------------------------------------------------ */
  var onScroll = rafThrottle(function () {
    if (FULL) { updateParallax(); updateHero(); updateProgress(); }
  });

  function initScope(scope) {
    initSplits(scope);
    tagReveals(scope);
    initParallax(scope);
    initCounts(scope);
    initDragRails(scope);
    initDetails(scope);
  }

  function boot() {
    if (MODE === 'none' || MODE === 'off') { initDragRails(document); return; }
    initProgress();
    initScope(document);
    initMagnetic();
    initSpotlight();
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    var queued = false;
    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            if (queued) return;
            queued = true;
            setTimeout(function () { queued = false; tagReveals(document); initDragRails(document); }, 120);
            return;
          }
        }
      }).observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener('shopify:section:load', function (e) {
      initScope(e.target);
      e.target.querySelectorAll('[data-liora-split]').forEach(function (el) { el.classList.add('is-inview'); });
      if (window.LioraTheme && window.LioraTheme.revealAll) window.LioraTheme.revealAll(e.target);
    });
    document.addEventListener('shopify:section:select', function (e) {
      e.target.querySelectorAll('[data-liora-split]').forEach(function (el) { el.classList.add('is-inview'); });
    });
    document.addEventListener('shopify:section:unload', function (e) {
      e.target.querySelectorAll('[data-liora-parallax]').forEach(function (el) {
        parallaxActive.delete(el);
        if (parallaxObserver) parallaxObserver.unobserve(el);
      });
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
